/**
 * GENERATED from source/types-of-constraints.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const TYPES_OF_CONSTRAINTS = {
  "key": "types-of-constraints",
  "title": "Types of Constraints",
  "category": "Concepts",
  "platform": {
    "key": "types-of-constraints",
    "sub": "Design the Problem. Let the Athlete Solve It.",
    "duration": "8–10 min read",
    "chapter": {
      "id": "types-of-constraints",
      "title": "Types of Constraints"
    }
  },
  "cert": {
    "key": "types-of-constraints-cert",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "intro",
      "title": "What Is a Constraint?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "What Is a Constraint?"
        },
        {
          "kind": "text",
          "md": "A coach watches an athlete make the same decision over and over. The coach can stop the drill and tell the player what they should have done. Sometimes that's exactly the right move.\n\nBut there's another option.\n\n**Change the problem the athlete is solving.**\n\nInstead of giving them the answer, change the rules, the space, the opponent, the objective, or the reward so the athlete has a reason to explore another answer.\n\nThat's where constraints come in."
        },
        {
          "kind": "bigIdea",
          "md": "A constraint is anything that shapes the ways an athlete can solve a problem."
        },
        {
          "kind": "text",
          "md": "A constraint can take options away. It can make an option more attractive. It can change which solutions are possible or useful. And it doesn't have to be something a coach intentionally adds.\n\nThe athlete's body and confidence constrain behavior. The environment they grow up in does too. The rules of a particular game constrain it further.\n\nThe **Constraints-Led Approach (CLA)** is how we deliberately work with those factors as coaches:"
        },
        {
          "kind": "bigIdea",
          "md": "Set a goal. Shape the conditions. Let the athlete solve the problem. Watch what emerges. Adjust."
        },
        {
          "kind": "text",
          "md": "The athlete isn't necessarily figuring everything out alone. Coaching can still include demonstration, a cue, feedback, or an explanation. But instead of making verbal instruction our only tool, we allow the task to do more of the teaching."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A Goal, Boundaries, and Someone to Solve the Problem"
        },
        {
          "kind": "text",
          "md": "Imagine giving someone a destination and a course to get through.\n\nYou can build a course with wide-open paths. Or you can close certain paths and make others more attractive. You can change what counts as successfully finishing.\n\nThe person must find a way through.\n\nIn a coaching environment, we are working with three ingredients:\n\n**The goal:** What are we trying to develop or accomplish?\n\n**The conditions:** Which possibilities are available, restricted, or rewarded?\n\n**The athlete:** What do they actually perceive, attempt, and discover in response?\n\nThe goal might be very narrow: we want the athlete to explore a specific finish or movement organization.\n\nOr the goal might be broader: expose the group to a wider range of meaningful problems and see how they adapt.\n\nBoth can be intentional. **Not every good constraint points toward one predetermined answer.**\n\nAnd the course isn't set in stone. If athletes find an unintended solution, you might let it play out. You might close that route. Or you might realize you've discovered something more useful than what you originally planned."
        },
        {
          "kind": "bigIdea",
          "md": "The coach doesn't just design the task. The coach reads what the task is doing to the athlete."
        }
      ]
    },
    {
      "id": "places",
      "title": "The Three Places Constraints Come From",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "The Three Places Constraints Come From"
        },
        {
          "kind": "text",
          "md": "Scientists commonly group constraints into three categories."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Individual constraints: What the athlete brings"
        },
        {
          "kind": "text",
          "md": "Limb length. Strength. Mobility. Coordination. Existing skills. Confidence. Beliefs. Fatigue. Previous experience.\n\nThese help explain why two players can encounter the same situation and solve it differently.\n\nThey are not all permanent. Years of movement experience can change an athlete's coordination, expectations, confidence, and available solutions."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Environmental constraints: The world around the athlete"
        },
        {
          "kind": "text",
          "md": "Facilities. Surfaces. Weather. Access to play. Social and cultural environments. The conditions athletes repeatedly experience while growing up.\n\nIn The Modern Basketball Blueprint, BAM discusses how different facilities, limited access, unusual hoops, and playing environments may shape the kinds of problems athletes encounter. Those are examples of environmental influence, not proof that one particular upbringing causes an elite skill."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Task constraints: The problem we're asking them to solve"
        },
        {
          "kind": "text",
          "md": "Rules. Goals. Equipment. Playing numbers. Space. Starting conditions. Scoring systems. What athletes are and aren't allowed to do.\n\n**Task constraints are usually the most immediately adjustable category for a coach.**\n\nOne nuance: these categories overlap. Growing up on a small court may be part of an athlete's environmental history. Deliberately shrinking the court today is a manipulation of the practice task. That history may then help shape their individual movement profile."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Deeper Foundations: Why Constraints Can Work",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The three-way distinction between **individual, environment, and task constraints** is commonly traced to Karl Newell's 1986 framework for understanding the development of coordination.\n\nThis is not the same framework as BAM's **Remove / Force / Reprice** levers. Newell's categories explain the sources of constraints acting on behavior. BAM's levers are a practical way to choose how to deliberately modify a task.\n\nIn an ecological-dynamics account, movement and decisions emerge from the interaction of athlete, task, and environment. A coach can therefore influence behavior by changing those relationships rather than explicitly specifying every movement component.\n\nA useful mechanism is **self-organization**. When athletes experience a different task problem, they may find a functional movement coordination or decision pattern through interaction with it.\n\nThat can create learning opportunities for three reasons:\n\n**1. The athlete has to solve rather than only reproduce.** Some useful movement organizations are discovered in response to task demands.\n\n**2. Relevant information can remain linked to action.** A live defender can shape which options are available in a way a verbal cue alone may not.\n\n**3. A useful change can be experienced rather than only described.** The athlete feels or perceives the consequences of different solutions.\n\nThese are theoretically grounded rationales, **not evidence that every constraint causes superior learning**. The athlete might discover a poor workaround, fail to notice the target information, or only perform well under the artificial rule.\n\n**For the Science Nerds 🤓:** See Newell, 1986, Constraints on the Development of Coordination, [DOI](https://doi.org/10.1007/978-94-009-4460-2_19).",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Individual, Task, and Environmental Constraints Interact",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "One risk of presenting the three scientific categories as separate boxes is making them sound independent.\n\nThey aren't.\n\nAn athlete's limb length or ankle mobility changes which task solutions are feasible. Their confidence affects which solutions they trust. Repeated exposure to smaller courts or imperfect facilities may shape movement tendencies and tactical preferences over time.\n\nA coach could give identical constraints to two athletes and get completely different outcomes.\n\nThe Modern Basketball Blueprint explores how imperfect facilities, restricted access, and diverse playing environments may shape athlete development. Those are useful coaching observations and plausible developmental influences, **not proof that one childhood experience directly causes an elite basketball skill**.\n\nA useful consequence for coaches:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Don't copy a constraint from one athlete and assume it will create the same learning problem for another.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "levers",
      "title": "Three Levers for Designing Better Tasks",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Three Levers for Designing Better Tasks"
        },
        {
          "kind": "text",
          "md": "Understanding where constraints come from is useful. But the more practical coaching question is:\n\n**What can I change right now?**\n\nBAM uses three levers for intentionally changing the task:"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "1. REMOVE: “You can't.”"
        },
        {
          "kind": "text",
          "md": "Take an option away.\n\nIf a player repeatedly relies on one solution, removing it temporarily can make them search for another.\n\nFor instance, BAM has used games where a preferred dribble action is unavailable, or where an athlete is restricted from using a familiar finishing option so they must explore something else.\n\n**Best starting use:** An athlete keeps leaning on a solution they already know."
        },
        {
          "kind": "bigIdea",
          "md": "Remove changes what they can choose from."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "2. FORCE: “You must.”"
        },
        {
          "kind": "text",
          "md": "Require a particular action or condition.\n\nMaybe an athlete has almost no experience using a two-foot finish. Requiring two feet for a short activity can expose them to a movement solution that otherwise never appears.\n\nThis can be powerful. But be careful: doing something because a rule requires it is different from recognizing when to do it in a game.\n\n**Best starting use:** The behavior does not appear at all yet, and we want purposeful exposure."
        },
        {
          "kind": "bigIdea",
          "md": "Force changes whether they choose at all."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "3. REPRICE: “You still can, but now it's worth something different.”"
        },
        {
          "kind": "text",
          "md": "Keep the actions legal. Change their payoff.\n\nIn The Modern Basketball Blueprint, BAM describes giving a floater more points than other finishes when the skill exists but players aren't choosing it. A player now has a reason to look for the floater without being prohibited from using another finish.\n\nRewards can be positive, or you can disincentivize behavior through penalties. The logic is the same: change the consequences, not necessarily the legal options.\n\n**Best starting use:** The athlete can use a solution, but doesn't choose it often enough."
        },
        {
          "kind": "bigIdea",
          "md": "Reprice changes what they choose."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Which Lever Should I Use?"
        },
        {
          "kind": "text",
          "md": "The most useful starting question isn't “Which constraint is most creative?”\n\nIt's:\n\n**What is happening with the behavior I'm trying to develop?**\n\n- **It already exists, but the athlete rarely chooses it:** Consider **Reprice**.\n- **The athlete repeatedly falls back on one default:** Consider **Remove**.\n- **The athlete never even experiences an important option:** Consider **Force** for exposure, while checking whether they have the action capacity to perform it.\n\nThose are starting points, not laws. Sometimes removing an option is too extreme. Sometimes an incentive isn't strong enough. Sometimes the athlete literally cannot execute what you're requiring.\n\nBAM generally prefers to **Reprice** when we can, because players retain more freedom to read and choose within the task.\n\nBut that doesn't mean Reprice always makes practice more representative. A very large scoring bonus can still cause athletes to chase something the game wouldn't normally reward."
        },
        {
          "kind": "bigIdea",
          "md": "The best lever is the one that brings the learning problem into focus without distorting the experience more than necessary."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Full BAM How × What Grid",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Remove, Force, and Reprice describe **how** we change a task. The next question is:\n\n**What exactly are we changing?**\n\nThe eight common targets in BAM's working design map are:\n\n1. **Space:** Where the activity occurs and how much room is available.\n2. **Time:** How quickly something must happen and when an action becomes available.\n3. **Numbers:** How many offensive / defensive participants or numerical advantages exist.\n4. **Effector (body):** Which limbs, postures, or movement options are available.\n5. **Information:** What the athlete can see, hear, or perceive about the problem.\n6. **Sequencing:** Which actions or events must precede or follow others.\n7. **Opponent:** What the other player is allowed or required to do.\n8. **Task goal / payoffs:** What counts as success and what earns points or rewards.\n\n**How × What** is a design tool, not an invitation to create 24 equally good drills.",
          "depth": "cert"
        },
        {
          "kind": "table",
          "head": [
            "WHAT changes",
            "REMOVE: take an option away",
            "FORCE: require an action / condition",
            "REPRICE: change the payoff"
          ],
          "rows": [
            {
              "cells": [
                "**Space**",
                "Close part of the usable court",
                "Require a prescribed starting area",
                "Reward a desired location-based action"
              ]
            },
            {
              "cells": [
                "**Time**",
                "Remove a comfortable timing window",
                "Require action within a set window",
                "Reward an action completed within a useful window"
              ]
            },
            {
              "cells": [
                "**Numbers**",
                "Remove a teammate / defender from the problem",
                "Start with a defined numerical advantage",
                "Change the value of outcomes under a chosen numbers setup"
              ]
            },
            {
              "cells": [
                "**Effector / body**",
                "No familiar finish or dribble option",
                "Two-foot finish only for a short block",
                "Reward use of the underselected movement option"
              ]
            },
            {
              "cells": [
                "**Information**",
                "Temporarily withhold one information source",
                "Require an information condition in the setup",
                "Reward responses contingent on information that appears"
              ]
            },
            {
              "cells": [
                "**Sequencing**",
                "Disallow a familiar first action",
                "Require an entry condition; beware five-pass choreography",
                "Reward a setup action that creates the next opportunity"
              ]
            },
            {
              "cells": [
                "**Opponent**",
                "Deny a defensive action",
                "Defense must ICE the screen",
                "Adjust payoff so a live defensive response is worth more"
              ]
            },
            {
              "cells": [
                "**Goal / payoffs**",
                "Remove one scoring route",
                "Require a specified goal for that block",
                "A paint touch makes the next three worth four"
              ]
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Why this grid is useful",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Instead of saying, “I need another finishing drill,” a coach can start with the learning problem:\n\n- Which option should become more available?\n- What gets in its way?\n- Do we need to remove, force, or reprice?\n- Which part of the task should change to accomplish that?\n\nThat shifts us from **collecting drills** to **designing them**.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "rule",
      "title": "A Rule Isn't Good Just Because It Works",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Rule Isn't Good Just Because It Works"
        },
        {
          "kind": "text",
          "md": "Imagine telling a team they must complete five passes before they're allowed to shoot.\n\nThey pass five times.\n\nTechnically, the constraint worked.\n\nBut what did they learn?\n\nThey may have simply counted to five. They may have passed up a great shot. They may be playing the rule instead of solving the basketball problem.\n\nThis is a common risk with **Force**.\n\nOne useful alternative is to force an **entry condition** rather than an entire scripted possession. Establish the problem you want the athlete to encounter, then let the rest of the possession be live.\n\nThat doesn't make every forced rule wrong. If the specific purpose is technical exposure or temporarily narrowing a solution space, a forced action may be perfectly appropriate.\n\nThe key is to be clear on the purpose."
        },
        {
          "kind": "bigIdea",
          "md": "Did the rule produce the behavior, or did it help develop the athlete's ability to solve the problem?"
        },
        {
          "kind": "text",
          "md": "Those can be different outcomes."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Why Force × Sequencing Is a Trap",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Not all grid cells are equally useful.\n\nForcing a particular sequence across an entire possession may create a predictable script. A rule requiring five passes before a shot is the obvious example: the athletes can satisfy the sequence without improving their ability to recognize a scoring opportunity.\n\nThere are still useful reasons to manipulate sequence.\n\nThe safer distinction is often:\n\n**Force an entry condition:** Shape the initial problem, then open the rest of the situation.\n\nversus\n\n**Force the whole solution:** Tell the athlete what must happen after each part of the problem.\n\nWhen the objective is live perception and decision-making, the first is often more useful.\n\nWhen the objective is a narrow technical movement exposure, the second might still serve a purpose.\n\nThe coaching mistake is not using Force. It's **forgetting what Force is doing to the decision.**",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "opponent",
      "title": "Don't Forget the Opponent",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Don't Forget the Opponent"
        },
        {
          "kind": "text",
          "md": "One of BAM's favorite ways to shape practice is to constrain the **opponent**, not the learner.\n\nSay we want a ball handler to get repeated live exposure to ICE coverage on a ball screen.\n\nInstead of prescribing the offensive player's response, require the defense to ICE the screen.\n\nNow the offense experiences the relevant information repeatedly while still having choices to make.\n\nThe offensive player can learn how different solutions interact with that coverage, rather than simply memorizing one scripted counter.\n\nThis is especially useful when the goal is to train **reading and responding**, not just producing a movement on command.\n\nBut even this has a limitation: if the defense behaves too predictably and unnaturally, the athlete may start learning that practice-specific pattern instead of reading real basketball information."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Advanced Move 1: Constrain the Opponent, Not the Learner",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Constraining the opponent is especially useful when you want repeated exposure to a specific defensive coverage.\n\n**Purpose:** Expose the offensive player to the same family of live defensive information many times.\n\n**Constraint:** The defense must ICE the ball screen.\n\n**Freedom preserved:** The offensive player can find different answers to that problem rather than being told one mandatory response.\n\nThis doesn't guarantee the learning target is reached. The defense still needs to behave convincingly, and the coach needs to check what the offense is perceiving and deciding.\n\nThis is also a reminder that who we constrain matters as much as what we constrain.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Advanced Move 2: Conditional Repricing",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Instead of permanently making a certain action worth more points, change the scoring system **after a trigger occurs**.\n\nBAM example:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "A paint touch makes the next three worth four points.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The athlete now has a reason to seek the first action because of the opportunity it creates for the next one.\n\nThis can encourage a useful sequence of play without requiring an exact script.\n\nBut there is an unintended-consequence question: does the incentive make athletes look for meaningful paint touches, or do they start hunting a meaningless technicality just to unlock the points?\n\nThat is why we always evaluate behavior, not just compliance with the scoring system.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Advanced Move 3: Rolling Constraints",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Change a constraint while an activity is underway.\n\nThis can force the athletes to reorganize when the solution that was attractive a moment ago is suddenly less attractive.\n\nPotential use: building adaptability to changed task demands.\n\nPotential cost: players may devote more attention to remembering the new rule than to reading the basketball information we care about.\n\nIf the rule-change itself is the main challenge, that's fine when adaptation to shifting rules is the goal. But don't assume that makes perception more game-like.\n\nA rolling constraint is a tool. Not a default.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Hidden Cost of Incentives",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Repricing can be a beautiful tool because the player still has a choice.\n\nBut scoring rules can create new problems.\n\n**1. The defense learns the incentive.** It may sell out to stop the rewarded option, altering the offense's read. That can be useful, or it can make the task unrealistic.\n\n**2. Different players get different difficulty.** A bonus may be easy for one athlete and nearly unreachable for another because of their skill profile.\n\n**3. The athlete starts playing the scoring system.** The rule replaces the game as the thing they are trying to read.\n\n**4. Too many bonuses overload attention.** The player has to remember the scoring math instead of solving the game.\n\nIn The Modern Basketball Blueprint, BAM's practical rule of thumb is to use **no more than roughly two point adjustments** in a single game. That is a heuristic about cognitive clutter, not a research-established numerical cutoff.\n\nThe book also describes player-specific scoring adjustments and occasionally keeping each side's incentive unknown to the other side.\n\nA useful coach question:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Has the reward created better decisions, or simply a new way to game the activity?",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "living",
      "title": "Constraints Are a Living Coaching Tool",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Constraints Are a Living Coaching Tool"
        },
        {
          "kind": "text",
          "md": "A player finds a clever workaround. A constraint creates too much frustration. The defender starts exploiting the point system. The target skill is no longer getting meaningful reps.\n\nYou have options.\n\nOpen the boundaries a little.\n\nClose a different option.\n\nChange the payoff.\n\nConstrain the opponent instead.\n\nGive a demonstration or cue.\n\nTake away the constraint and see what remains.\n\nThis is guided discovery in practice: not standing silently while athletes struggle forever, and not immediately prescribing every answer either.\n\nBAM generally likes to let the athlete solve first. But we adjust how much guidance we provide based on the athlete, the problem, their body language, and the time we have."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Discovery, Autonomy, and Feedback Aren't the Same Thing",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Coaches sometimes bundle three claims together:\n\n1. Athletes should have some freedom to explore.\n2. Athletes should feel autonomy and ownership.\n3. Coaches should avoid direct instruction and feedback.\n\nThese are **not** the same claim.\n\nAn athlete could experience many movement possibilities inside a task that is still completely controlled by the coach. Or the coach could use an explicit demonstration while also supporting the athlete's ownership of the process.\n\nA 2022 systematic review and meta-analysis of coach autonomy support in sport and exercise analyzed **131 independent samples and 38,844 participants**. Autonomy support was positively associated with autonomous motivation, basic psychological needs, and well-being. That is meaningful psychological evidence, but predominantly **associational**, not a direct demonstration that a CLA drill outperforms technical instruction in long-term basketball skill learning.\n\nBAM's applied position:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Give the athlete room to experience the problem. Then coach as much or as little as the situation demands.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "In Deep Skill, guided discovery begins by giving athletes an opportunity to try. When needed, the coach can add a demonstration, a short cue, a break, or a more detailed explanation. The goal isn't to avoid instruction; it's to give the athlete the guidance the situation calls for.\n\n**Research:** [Autonomy support systematic review and meta-analysis (2022)](https://doi.org/10.1080/1750984X.2022.2031252). **BAM source:** Deep Skill, Chapter 9, Implicit Learning & Guided Discovery, approximately printed pp. 85–89.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "test",
      "title": "The Most Important Test: What Happens Without the Rule?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "The Most Important Test: What Happens Without the Rule?"
        },
        {
          "kind": "text",
          "md": "If a player makes a two-foot finish when you tell them that's the only finish allowed, you've shown they can attempt a two-foot finish **under that constraint**.\n\nYou haven't yet shown they recognize when it is useful.\n\nIf a player shoots more floaters when floaters are worth extra points, you've changed their decisions **under that scoring system**.\n\nYou haven't necessarily changed their decisions in games.\n\nEventually, we need to loosen or remove the artificial rule and give the athlete relevant information again.\n\nCan they still access the skill?\n\nCan they recognize when it fits?\n\nCan they use it against a live opponent?\n\nDoes it hold up later, not just five minutes after the drill?"
        },
        {
          "kind": "bigIdea",
          "md": "Use constraints to open the door. Then see whether the athlete walks through it when nobody tells them to."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "CLA, Representativeness, and Technical Change",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The fact that an activity uses constraints does not mean it is representative of the game.\n\nAnd the fact that an activity happens **on air** does not mean it cannot be intentionally constraints-led.\n\nBAM sometimes uses a constraint to open up or stabilize a technical coordination that would be difficult to target in live play. That may be the correct intervention for that athlete at that moment.\n\nOther times, when the goal is reading a defender, we want to preserve the specifying information that makes the decision meaningful.\n\nTwo different questions:\n\n**Is this constraint helping me develop the movement capacity?**\n\n**Is this constraint helping the athlete perceive and act on the information they'll face in a game?**\n\nSometimes the same task does both. Sometimes we deliberately separate them and reconnect them later.\n\nThe danger is expecting nonrepresentative technical work to automatically produce stronger game perception, or assuming that merely adding a defender ensures meaningful learning.\n\nThe process is straightforward: set a clear purpose, create repeated exposure to the target problem, use feedback to adjust, and test whether the improvement transfers to the game.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "What Does the Sport Research Actually Support?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The research helps us understand where nonlinear approaches may be useful, but it doesn't establish that one coaching philosophy is best for every athlete, skill, or situation.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Team invasion sports: Promising for tactical learning",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A 2025 systematic review compared linear and nonlinear pedagogical approaches in team invasion ball sports. It found only **nine eligible studies**. For most technical outcomes, there were no statistically significant differences; nonlinear approaches were favored for **34% of technical outcomes**. Among the tactical outcomes covered by four studies, nonlinear approaches were favored for **66% of outcomes**.\n\nThose numbers refer to **the proportion of measured outcomes**, not the proportion of athletes improved or a measured percentage improvement. The evidence base was small and heterogeneous.\n\nA fair summary is:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Nonlinear approaches look promising for tactical development, but we do not have a universal winner for technical skill learning.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Source: [Bromilow et al., Sports Medicine - Open, 2025](https://doi.org/10.1186/s40798-025-00893-y).",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Basketball: Changing the rule changes behavior",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A 2024 review of basketball small-sided games synthesized **41 studies involving 922 participants**. Different player numbers and constraints affected technical and tactical behaviors. Smaller formats generally increased action opportunities. Restrictions on dribbling were associated with more passing actions.\n\nThis supports the practical premise that **task constraints can influence what athletes do**.\n\nBut many studies measured behavior **during the manipulated game**, not long-term retention or transfer to normal competition.",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Eliciting the behavior is not the same as proving the behavior was learned.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Source: [Souza et al., Retos, 2024](https://doi.org/10.47197/retos.v56.104564).",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "What this does not tell us",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "These studies do not prove that:\n\n- Reprice beats Remove or Force for learning.\n- Every game-like drill is automatically representative.\n- Athletes always learn best without explanation.\n- More creative constraints produce greater transfer.\n- Any single constraint works equally well for all athletes.\n\n**BAM's Remove / Force / Reprice classification remains a coaching design framework, not a scientifically validated three-part taxonomy.**",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Six-Step BAM Constraint Design Audit",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "1. Name the actual target",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "What action, decision, coordination, or capacity do you want to change?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "2. Diagnose the current behavior",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Does the athlete lack the option? Default away from it? Or have it but underselect it?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "3. Choose the lever",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Force**, **Remove**, or **Reprice**. Pick based on the problem rather than novelty.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "4. Choose the target",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Which element of the task will you manipulate: space, time, numbers, body, information, sequence, opponent, or goal?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "5. Observe the side effects",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "What actually happened? Did the target behavior appear? Did the athlete exploit the rule? Did the wrong part of the task become the main challenge?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "6. Fade and test",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Relax or remove the constraint. Does the athlete still access the useful solution with relevant information, and do they select it when appropriate?\n\nThe value of the framework is in the decisions it helps you make, not the number of creative rules you can invent.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity1",
      "title": "Activity 1: Diagnose Before Choosing",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 1: Diagnose Before Choosing",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "diagnose",
          "title": false,
          "numbered": false,
          "intro": "Use a real athlete or group you coach.",
          "steps": [
            {
              "id": "behavior",
              "q": "Part A. Identify a behavior that appears too infrequently.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "fits",
              "q": "Part B. Decide which best fits:",
              "hint": "- The athlete cannot currently do it.\n- They can do it, but a familiar option dominates.\n- They can do it, but the payoff or perceived usefulness isn't high enough.",
              "rows": 3
            },
            {
              "id": "lever",
              "q": "Part C. Choose Remove, Force, or Reprice. Explain the reason.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "backfire",
              "q": "Part D. Identify one reason your chosen lever could backfire.",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Check your reasoning:** Did you choose that lever because it fits the athlete's problem, or because it makes the drill sound creative?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 2: Three Levers, Same Goal",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "levers",
          "title": false,
          "numbered": false,
          "intro": "Consider a group that has practiced floaters but rarely chooses them in live play.\n\nDesign three versions using the same goal:",
          "steps": [
            {
              "id": "remove",
              "q": "Remove:",
              "hint": "What familiar option would you temporarily make unavailable? What could the player explore instead?",
              "rows": 3
            },
            {
              "id": "force",
              "q": "Force:",
              "hint": "What would you require for a brief exposure block? What basketball information would that remove from the decision?",
              "rows": 3
            },
            {
              "id": "reprice",
              "q": "Reprice:",
              "hint": "How would you use point values to invite the floater without prohibiting alternatives?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Then answer:",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "bestfit",
          "prompt": "Which version best fits this group's actual problem and why?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Do not assume all three are equally good.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 3: The How × What Design Grid",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "grid",
          "title": false,
          "numbered": false,
          "intro": "",
          "steps": [
            {
              "id": "target",
              "q": "Take an activity you already coach. First write down the actual learning target.",
              "hint": "",
              "rows": 2
            },
            {
              "id": "candidates",
              "q": "Choose two of the eight task targets. For each, propose two different levers. That creates four candidate modifications.",
              "hint": "",
              "rows": 4
            },
            {
              "id": "mark",
              "q": "Then mark each:",
              "hint": "- **Likely useful**\n- **Potentially useful but high risk of distortion**\n- **Unnecessary / not appropriate**",
              "rows": 4
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Do not try to fill all 24 cells.\n\n**Check your reasoning:** Which of your four modifications actually addresses the learning target, and which adds complexity without a clear benefit?",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity4",
      "title": "Activity 4: The Five-Pass Trap",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 4: The Five-Pass Trap",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "fivepass",
          "title": false,
          "numbered": false,
          "intro": "Take the forced five-pass rule.",
          "steps": [
            {
              "id": "guarantees",
              "q": "**1.** What does this rule guarantee?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "not",
              "q": "**2.** What does it **not** guarantee?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "entry",
              "q": "**3.** If the real learning target is an offensive decision, could an entry condition create that situation while leaving the eventual decision live?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "progress",
              "q": "**4.** What would you need to see after removing the rule to claim progress?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This is an analysis exercise. Do not invent a full new drill unless you can ground it in a situation you actually coach.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 5: Audit a Conditional Incentive",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Use the BAM example:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "A paint touch makes the next three worth four.",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "incentive",
          "title": false,
          "numbered": false,
          "intro": "Explain:",
          "steps": [
            {
              "id": "encourage",
              "q": "What behavior does this incentive try to encourage?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "unintended",
              "q": "What unintended behaviors could it reward?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "defense",
              "q": "What might the defense do in response?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "meaningful",
              "q": "How would you know the paint touch is meaningful rather than just a trigger the offense is gaming?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "remove",
              "q": "How would you later remove the bonus and assess transfer?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Check your reasoning:** Does the incentive improve the behavior you care about, or only change what earns points?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 6: From Constrained Behavior to Game Choice",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "transfer",
          "title": false,
          "numbered": false,
          "intro": "Choose a real BAM or personal practice activity where you've required or rewarded one option.\n\nDescribe three steps:",
          "steps": [
            {
              "id": "exposure",
              "q": "Exposure:",
              "hint": "What constraint makes the option appear?",
              "rows": 3
            },
            {
              "id": "transition",
              "q": "Transition:",
              "hint": "How will you reduce the artificial support while retaining useful repetitions?",
              "rows": 3
            },
            {
              "id": "check",
              "q": "Transfer check:",
              "hint": "How will you recognize the athlete choosing it at the right time in a less-constrained, representative situation?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Include a follow-up observation after a later session if possible. Do not use success inside the original constrained drill as your only evidence.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "takeaway",
      "title": "Final Takeaway",
      "blocks": [
        {
          "kind": "reflection",
          "md": "Think of one behavior you want to see more often from an athlete.\n\n**Do they lack the option, rely too heavily on another option, or already have the option but rarely choose it?**\n\nWould you **Force**, **Remove**, or **Reprice**?\n\nNow ask the bigger question: **what would you need to see after removing the constraint to know the behavior actually transferred?**",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "reflection",
          "title": false,
          "numbered": false,
          "intro": "Think about how you use constraints today.",
          "steps": [
            {
              "id": "overuse",
              "q": "Which lever do you overuse?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "underuse",
              "q": "Which do you underuse?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "wonthedrill",
              "q": "Have you ever created a drill where athletes learned to win the drill but didn't improve at the basketball problem?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "clue",
              "q": "What was the clue that exposed the difference?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "And most importantly:",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "tomorrow",
          "prompt": "Am I using a rule to produce the response I want to see today, or designing an experience that helps the athlete choose a better response tomorrow?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway"
        },
        {
          "kind": "text",
          "md": "Constraints are not just rules that make a drill more difficult.\n\nThey're the conditions shaping what athletes see, what they can do, and which solutions become useful.\n\nAs coaches, we can use those conditions intentionally:\n\n**Remove:** Take away a familiar option.\n\n**Force:** Require exposure to a specific behavior.\n\n**Reprice:** Make an underused option more attractive.\n\nBut the process matters more than the trick."
        },
        {
          "kind": "bigIdea",
          "md": "Start with a purpose. Design the problem. Observe the solution. Adjust the constraints. Then test what survives when the rules go away."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Sources and Evidence Notes",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**BAM primary sources**\n\n- Coleman Ayers, The Modern Basketball Blueprint, introduction and sections on constraints in representative practice and effective on-air training, especially printed pp. 5, 57–70, and 122–124.\n- Coleman Ayers, Deep Skill, Chapter 9 (guided discovery) and Chapter 12 (Games Approach, printed pp. 157–158).\n\n**Scientific sources**\n\n- Newell, K. M. (1986). [Constraints on the Development of Coordination](https://doi.org/10.1007/978-94-009-4460-2_19). Foundational individual/task/environment framing.\n- Bromilow, L. et al. (2025). [The Effectiveness of Linear and Nonlinear Pedagogical Approaches in Team-Invasion Ball Sports: A Systematic Review](https://doi.org/10.1186/s40798-025-00893-y). Small body of evidence, promising tactical outcomes; no universal technical winner.\n- Souza, W. J. F. et al. (2024). [Tactical and Technical Performance in Basketball Small-Sided Games: A Systematic Review](https://doi.org/10.47197/retos.v56.104564). Acute basketball behavior changes under varied game formats and constraints, not definitive proof of long-term transfer.\n- [Autonomy support in sport and exercise settings: A systematic review and meta-analysis](https://doi.org/10.1080/1750984X.2022.2031252) (2022). Evidence for psychological associations; not evidence that every CLA drill leads to greater learning.",
          "depth": "cert"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            },
            {
              "key": "degrees-of-freedom",
              "title": "Degrees of Freedom",
              "blurb": "Freedom Is Only Useful If You Can Organize It."
            },
            {
              "key": "challenge-point",
              "title": "Challenge Point & Desirable Difficulties",
              "blurb": "Hard Is Not the Goal. Productive Struggle Is."
            },
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            },
            {
              "key": "differential-learning-concept",
              "title": "Differential Learning",
              "blurb": "Push the Borders. Reorganize the Skill."
            },
            {
              "key": "attractors-metastability",
              "title": "Attractors, Multistability & Metastability",
              "blurb": "Stable Enough to Trust. Flexible Enough to Adapt."
            }
          ]
        }
      ]
    }
  ]
};
